import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Spin } from 'antd';
import {
  LoadingOutlined, InfoCircleOutlined, ExportOutlined,
  CheckOutlined, FolderOpenOutlined,
} from '@ant-design/icons';
import T from './theme';
import { statutToStep, STEP_LABELS } from './messagerieStatut';
import { STATUTS_SINISTRE } from '../../variables/constants';
import { libellesTraduits } from '../../i18n/libelles';

const WIDTH = 300;

// Type sinistre -> mockup-friendly short label.
const TYPE_LABELS = libellesTraduits('messagerie:fiche.types');

function EmptyFiche() {
  const { t } = useTranslation('messagerie');
  return (
    <aside style={{ width: WIDTH, flexShrink: 0, background: T.contextBg,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 24, textAlign: 'center' }}>
      <div>
        <InfoCircleOutlined style={{ fontSize: 26, color: '#cbd5e1' }} />
        <div style={{ fontSize: 12.5, fontWeight: 700, color: '#64748b', marginTop: 8 }}>
          {t('fiche.aucunLien')}
        </div>
        <div style={{ fontSize: 11, color: T.textMut, marginTop: 4, lineHeight: 1.5 }}>
          {t('fiche.aucunLienAide')}
        </div>
      </div>
    </aside>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: 9, color: T.textMut, fontWeight: 700, letterSpacing: '.3px' }}>{label}</div>
      <div style={{ fontSize: 12.5, fontWeight: 700, color: '#334155' }}>{value || '-'}</div>
    </div>
  );
}

export default function FicheSinistre({ sinistre, loading }) {
  const { t } = useTranslation('messagerie');
  const navigate = useNavigate();

  if (loading) {
    return (
      <aside style={{ width: WIDTH, flexShrink: 0, background: T.contextBg,
        display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Spin indicator={<LoadingOutlined style={{ fontSize: 22, color: T.green }} spin />} />
      </aside>
    );
  }

  if (!sinistre) return <EmptyFiche />;

  const numero = sinistre.numeroSinistreLocal || '-';
  const statut = sinistre.statut || '';
  const statutLabel = STATUTS_SINISTRE.find((s) => s.value === statut)?.label || statut || '-';
  const typeLabel = TYPE_LABELS[sinistre.typeSinistre] || sinistre.typeSinistre || '-';
  const assure = [sinistre.nomAssure, sinistre.prenomAssure].filter(Boolean).join(' ') || '-';
  const vehicule = sinistre.assureImmatriculation || '-';
  const step = statutToStep(statut);

  return (
    <aside className="cb-scroll" style={{ width: WIDTH, flexShrink: 0, background: T.contextBg,
      overflowY: 'auto', padding: 20,
      display: 'flex', flexDirection: 'column', gap: 16 }}>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        borderBottom: `1px solid ${T.border}`, paddingBottom: 12 }}>
        <span style={{ fontSize: 10, fontWeight: 700, color: T.textMut,
          textTransform: 'uppercase', letterSpacing: '.5px' }}>{t('fiche.titre')}</span>
        <span style={{ background: T.badge.nature.bg, color: T.badge.nature.fg,
          fontSize: 9, fontWeight: 700, padding: '2px 8px', borderRadius: 999 }}>{statutLabel}</span>
      </div>

      <div>
        <div style={{ fontSize: 10, color: T.textMut, fontWeight: 700 }}>{t('fiche.numero')}</div>
        <div onClick={() => navigate(`/sinistres/${sinistre.sinistreTrackingId}`)}
          style={{ fontSize: 14, fontWeight: 700, color: '#334155', cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 6 }}>
          {numero} <ExportOutlined style={{ fontSize: 11 }} />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12,
        background: T.panel, padding: 12, borderRadius: T.radius,
        border: `1px solid ${T.border}`, boxShadow: T.shadowSm }}>
        <Field label={t('fiche.type')} value={typeLabel} />
        <Field label={t('fiche.paysGestionnaire')} value={sinistre.paysEmetteurLibelle} />
        <div style={{ gridColumn: '1 / -1', borderTop: `1px solid ${T.borderSoft}`, paddingTop: 8 }}>
          <Field label={t('fiche.assure')} value={assure} />
        </div>
        <div style={{ gridColumn: '1 / -1' }}>
          <Field label={t('fiche.vehicule')} value={vehicule} />
        </div>
      </div>

      <div style={{ background: T.panel, padding: 14, borderRadius: T.radius,
        border: `1px solid ${T.border}`, boxShadow: T.shadowSm }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: '#64748b', marginBottom: 12 }}>
          {t('fiche.avancement')}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {STEP_LABELS.map((label, i) => {
            const done = step.index > i;
            const active = step.index === i;
            const bg = done ? T.accent : active ? '#f59e0b' : '#e2e8f0';
            const fg = done || active ? '#fff' : '#64748b';
            return (
              <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 8,
                opacity: !done && !active ? .5 : 1 }}>
                <div style={{ width: 20, height: 20, borderRadius: '50%', background: bg, color: fg,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10 }}>
                  {done ? <CheckOutlined /> : active
                    ? <LoadingOutlined spin /> : i + 1}
                </div>
                <span style={{ fontSize: 12, fontWeight: active ? 700 : 500,
                  color: active ? '#1e293b' : '#475569' }}>{label}</span>
              </div>
            );
          })}
        </div>
        <button onClick={() => navigate(`/sinistres/${sinistre.sinistreTrackingId}`)}
          style={{ width: '100%', marginTop: 14, padding: '8px 0', border: 'none',
            background: T.accent, color: '#fff', borderRadius: T.radius, fontWeight: 700,
            fontSize: 11, cursor: 'pointer', fontFamily: T.font,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
          <FolderOpenOutlined /> {t('fiche.ouvrirGed')}
        </button>
      </div>
    </aside>
  );
}
